import { createTheme, ThemeOptions } from "@mui/material/styles";

import { COLORS } from "../../constants/colors";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    caption2: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    caption2?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    caption2: true;
  }
}

declare module "@mui/material/styles/createPalette" {
  interface Palette {
    delete: { main: string; contrastText?: string };
  }

  interface PaletteOptions {
    delete?: { main: string; contrastText?: string };
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    delete: true;
  }
}

/**
 * utility function for deep merge
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isObject = (item: any): any => {
  return item && typeof item === "object" && !Array.isArray(item);
};

/**
 * deep merge default theme and custom theme provided by app
 */
const mergeDeep = (
  target: ThemeOptions,
  source: ThemeOptions
): ThemeOptions => {
  const output = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) Object.assign(output, { [key]: source[key] });
        else output[key] = mergeDeep(target[key], source[key]);
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
};

/**
 * default breakpoints
 */
const breakpoints = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 1024,
      md: 1200,
      lg: 1440,
      xl: 1800,
    },
  },
};

/**
 * merges custom breakpoints with default, then provides them to merged theme and custom theme
 * @returns generated theme from default and custom breakpoints and theme
 */
const generateTheme = (
  customTheme = {},
  customBreakpoints = {}
): ThemeOptions => {
  const mergedBreakpoints = mergeDeep(breakpoints, customBreakpoints);
  const breakpointTheme = createTheme(mergedBreakpoints);

  const theme = {
    palette: {
      primary: {
        main: COLORS.SAY_COBALT,
      },
      secondary: {
        // This is green.A700 as hex.
        main: "#11cb5f",
      },
      delete: {
        main: COLORS.SATURATED_RED,
        contrastText: COLORS.WHITE,
      },
    },
    breakpoints: breakpointTheme.breakpoints,
    typography: {
      fontFamily: ["Franklin Gothic", "Helvetica Neue", "sans-serif"].join(","),
      h1: {
        fontSize: "3.75rem",
        lineHeight: "4.5rem",
        letterSpacing: "-0.5px",
        [breakpointTheme.breakpoints.down("sm")]: {
          fontSize: "3rem",
          lineHeight: "3.5rem",
          letterSpacing: "0px",
        },
      },
      h2: {
        fontSize: "3rem",
        lineHeight: "3.5rem",
        letterSpacing: "0px",
        [breakpointTheme.breakpoints.down("sm")]: {
          fontSize: "2.25rem",
          lineHeight: "2.25rem",
          letterSpacing: "0.25px",
        },
      },
      h3: {
        fontSize: "2.25rem",
        lineHeight: "2.25rem",
        fontWeight: 500,
        letterSpacing: "0.25px",
        [breakpointTheme.breakpoints.down("sm")]: {
          fontSize: "1.5rem",
          lineHeight: "1.5rem",
          letterSpacing: "0.18px",
        },
      },
      h4: {
        fontSize: "1.5rem",
        fontWeight: 500,
        lineHeight: "1.5rem",
        letterSpacing: "0px",
        [breakpointTheme.breakpoints.down("sm")]: {
          fontSize: "1.25rem",
          lineHeight: "1.25rem",
          letterSpacing: "0.15px",
        },
      },
      h5: {
        fontSize: "1.25rem",
        lineHeight: "1.5rem",
        fontWeight: 500,
        letterSpacing: "0.15px",
      },
      subtitle1: {
        fontSize: "1rem", // 16
        lineHeight: "1.5rem",
        fontWeight: 500,
        letterSpacing: "0.15px",
      },
      subtitle2: {
        fontSize: "0.875rem", // 14
        lineHeight: "1.5rem",
        fontWeight: 500,
        letterSpacing: "0px",
      },
      body1: {
        fontSize: "1rem", // 16
        lineHeight: "1.5rem",
        letterSpacing: "0px",
      },
      body2: {
        fontSize: "0.875rem", // 14
        lineHeight: "1.25rem",
        letterSpacing: "0px",
      },
      overline: {
        fontSize: "0.625rem", // 10
        lineHeight: "1rem",
        letterSpacing: "1.5px",
        fontWeight: 500,
      },
      caption: {
        fontSize: "0.625rem", // 10
        lineHeight: "1rem",
        letterSpacing: "0.33px",
      },
      caption2: {
        fontSize: "0.75rem", // 12
        lineHeight: "1rem",
        letterSpacing: "0.4px",
      },
    },
    spacing: 4,
    components: {
      MuiTouchRipple: {
        styleOverrides: {
          root: {
            color: COLORS.SAY_COBALT,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: `
         * {
           box-sizing: border-box;
         }
         b {
           font-weight: 500;
         }
         li.public-DraftStyleDefault-orderedListItem {
            list-style-type: decimal;
          }
          li.public-DraftStyleDefault-orderedListItem:before {
          content: none;
          }`,
      },
      /*MuiOutlinedInput: { // deprecated in material v5 TBD: go add 12.5 in a different manner
        styleOverrides: {
          inputMarginDense: {
            paddingTop: "12.5px",
            paddingBottom: "12.5px",
          },
        },
      },*/
      MuiIconButton: {
        styleOverrides: {
          root: {
            fontFamily: [
              "Franklin Gothic",
              "Helvetica Neue",
              "sans-serif",
            ].join(","),
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            color: COLORS.WHITE,
            backgroundColor: COLORS.SAY_COBALT,
          },
          outlinedPrimary: {
            color: COLORS.BLACK,
          },
          textPrimary: {
            color: COLORS.BLACK,
          },
        },
      },
    },
  };

  const mergedTheme = mergeDeep(theme, customTheme);
  return createTheme(mergedTheme);
};

export default generateTheme;
